import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  FontWeight,
  Icon,
  IconNames,
  IconVariant,
  Space,
  Text,
} from '@gousto-internal/citrus-react'

import {
  emailPasswordRule,
  maxRule,
  minRule,
  oneLowerRule,
  oneSymbolOrNumberRule,
  oneUpperRule,
  passwordCriteria,
} from './errors'

type PasswordCriteriaProps = {
  password: string
  passwordErrors: string[]
  showFailedCriteria: boolean
  isEmailAndPassWordSame: boolean
}

type CriteriaStyle = {
  textColor: Color
  iconName: IconNames
  iconVariant: IconVariant
}

export const PasswordCriteria = ({
  password,
  passwordErrors,
  showFailedCriteria,
  isEmailAndPassWordSame,
}: PasswordCriteriaProps) => {
  const maxValue = passwordErrors.length > 0 && passwordErrors.includes(maxRule)
  const requirements = passwordCriteria.filter((item) =>
    maxValue ? item.rule !== minRule : item.rule !== maxRule,
  )

  const getFailedState = (rule: string): boolean => {
    const isWhiteSpaceOnly = password.trim() === ''
    if (!password) return true

    if (
      (isWhiteSpaceOnly && password.length < 8 && rule === minRule) ||
      (isWhiteSpaceOnly && [oneLowerRule, oneUpperRule, oneSymbolOrNumberRule].includes(rule)) ||
      (isEmailAndPassWordSame && rule === emailPasswordRule) ||
      passwordErrors.includes(rule)
    ) {
      return true
    }

    return false
  }

  const getCriteriaStyle = (rule: string): CriteriaStyle => {
    const isFailedRule = getFailedState(rule)

    if (isFailedRule) {
      return showFailedCriteria
        ? {
            textColor: Color.Error_800,
            iconName: 'close',
            iconVariant: IconVariant.Error,
          }
        : {
            textColor: Color.Inherit,
            iconName: 'bullet_point',
            iconVariant: IconVariant.Inherit,
          }
    } else {
      return {
        textColor: Color.Success_800,
        iconName: 'tick',
        iconVariant: IconVariant.Confirmation,
      }
    }
  }

  return (
    <Box data-testing="criteria-container">
      <Text size={1} fontWeight={FontWeight.Bold}>
        Your password must:
      </Text>
      <Space size={2} />
      <Box data-testing="errors-list" paddingBottom={4}>
        {requirements.map(({ message, rule }) => {
          const { textColor, iconName, iconVariant } = getCriteriaStyle(rule)

          return (
            <Box key={rule} display="flex" alignItems={AlignItems.Center}>
              <Icon name={iconName} variant={iconVariant} />
              <Space size={2} direction="horizontal" />
              <Text size={1} color={textColor}>
                {message}
              </Text>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
